import { Link } from "@tanstack/react-router";
import { AlertCircle, CheckCircle2, CloudOff, Loader2 } from "lucide-react";
import { useEffect, useState } from "react";

import { Button } from "components/ui/button";

import { raisedButtonClass } from "./button-styles";
import type { ConnectionRequestState } from "./use-connection-request";

function useOnline(): boolean {
  const [online, setOnline] = useState(() => navigator.onLine);
  useEffect(() => {
    const update = () => setOnline(navigator.onLine);
    window.addEventListener("online", update);
    window.addEventListener("offline", update);
    return () => {
      window.removeEventListener("online", update);
      window.removeEventListener("offline", update);
    };
  }, []);
  return online;
}

/** How a CONNECT went, for the scan sheet and the connect link page. */
export function ConnectResult({
  state,
  onAgain,
  againLabel = "Scan another code",
}: {
  state: ConnectionRequestState;
  onAgain?: () => void;
  againLabel?: string;
}) {
  const online = useOnline();
  if (state.status === "idle") return null;

  return (
    <div
      className="w-full space-y-4 rounded-lg border border-border bg-card p-4 text-card-foreground"
      data-testid="connect-result"
      data-status={state.status}
    >
      <div role="status" aria-live="polite" className="flex items-start gap-3">
        {(state.status === "queued" || state.status === "waiting") &&
          (state.status === "queued" && !online ? (
            <>
              <CloudOff className="mt-0.5 h-5 w-5 shrink-0 text-muted-foreground" />
              <p className="text-foreground">
                Will connect when you&apos;re back online.
              </p>
            </>
          ) : (
            <>
              <Loader2 className="mt-0.5 h-5 w-5 shrink-0 animate-spin text-muted-foreground" />
              <p className="text-foreground">Connecting…</p>
            </>
          ))}
        {state.status === "succeeded" && (
          <>
            <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-lit" />
            <p className="text-foreground" data-testid="connect-result-name">
              You are connected with{" "}
              <span className="font-semibold">
                {state.request.targetName ?? "them"}
              </span>
              .
            </p>
          </>
        )}
        {state.status === "failed" && (
          <>
            <AlertCircle className="mt-0.5 h-5 w-5 shrink-0 text-destructive-text dark:text-hb-red-text" />
            <p className="text-foreground" data-testid="connect-result-error">
              {state.error}
            </p>
          </>
        )}
      </div>
      <div className="flex flex-wrap gap-2">
        {(state.status === "succeeded" || state.status === "queued") && (
          <Button asChild className={raisedButtonClass}>
            <Link to="/connections">Open my connections</Link>
          </Button>
        )}
        {onAgain && state.status !== "waiting" && (
          <Button type="button" variant="outline" onClick={onAgain}>
            {againLabel}
          </Button>
        )}
      </div>
    </div>
  );
}
